import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import Badge from "./ui/Badge";
import Button from "./ui/Button";
import { clearWorkspaceId, getAuthToken, getWorkspaceId, getWorkspaceName, getWorkspaceRole, isAuthenticated, logout, refreshSession } from "../services/authStorage";
import { useAiStatus } from "../hooks/useAiStatus";
import AigisFloatingBubble from "./agent/AigisFloatingBubble";
import { useIntegrityAlerts } from "../hooks/useIntegrityAlerts";
import { setWorkspacePref, TOUR_PREF_KEY } from "../services/workspacePrefs";
import IntegrityBadge from "./IntegrityBadge";
import { TeleportProvider } from "../contexts/TeleportContext";
import { getApiBase } from "../services/apiBase";

import { APP_NAME, LOGO_URL, PRIMARY_COLOR } from "../config/branding";

const nav = [
  { to: "/exhibits", label: "Evidence Locker", icon: "fa-solid fa-box-archive", id: "nav-evidence" },
  { to: "/intake", label: "Intake Review", icon: "fa-solid fa-inbox", id: "nav-intake" },
  { to: "/assistant", label: "Meet Aigis", icon: "fa-solid fa-bolt", id: "nav-narrative" },
  { to: "/integrity", label: "Red Team Console", icon: "fa-solid fa-shield", id: "nav-integrity" },
  { to: "/security", label: "Security Audit", icon: "fa-solid fa-lock", id: "nav-security" },
  { to: "/compliance", label: "The Iron Grip", icon: "fa-solid fa-gavel", id: "nav-compliance" },
  { to: "/verification", label: "Admissibility Export", icon: "fa-solid fa-file-export", id: "nav-reports" },
  { to: "/roi", label: "ROI Dashboard", icon: "fa-solid fa-chart-line", id: "nav-roi" }
];

export default function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [authed, setAuthed] = useState(isAuthenticated());
  const [role, setRole] = useState(getWorkspaceRole());
  const workspaceId = getWorkspaceId();
  const workspaceName = getWorkspaceName() || "M&A Green Run";
  const ai = useAiStatus(authed);
  const { alert: integrityAlert, clear: clearIntegrityAlert } = useIntegrityAlerts();
  const [proofMode, setProofMode] = useState(false);
  const [notifications, setNotifications] = useState<Array<{ id: string; message: string }>>([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const isAdmin = useMemo(() => String(role || "").toLowerCase() === "admin", [role]);
  const notificationsEnabled = import.meta.env.VITE_ENABLE_NOTIFICATIONS === "1";
  const proofSteps = useMemo(() => ([
    { label: "Ingest Exhibit", action: () => navigate("/") },
    { label: "Ask Question", action: () => navigate("/assistant") },
    { label: "Verify Withheld/Anchored", action: () => navigate("/assistant") },
    { label: "Click Citation -> Teleport Highlight", action: () => navigate("/assistant") },
    { label: "Export Proof Packet", action: () => navigate("/admissibility") }
  ]), [navigate]);
  const aiStatus = ai.status;
  const aiHealth = aiStatus?.health || (authed ? "DEGRADED" : "OFFLINE");
  const aiBadgeTone = aiHealth === "OK" ? "green" : aiHealth === "DEGRADED" ? "amber" : "red";
  const aiLabel = aiStatus
    ? `AI: ${aiStatus.activeProvider || "unknown"} ${aiStatus.mode || "mode"} ${aiStatus.health || "CHECKING"}`
    : authed
      ? "AI: CHECKING"
      : "AI: OFFLINE";
  const aiTooltip = aiStatus
    ? [
        `Provider: ${aiStatus.activeProvider || "unknown"} (${aiStatus.mode || "mode"})`,
        `Health: ${aiStatus.health || "CHECKING"}`,
        `Primary model: ${aiStatus.models?.primary || "unknown"}`,
        `Audit: ${aiStatus.audit?.provider || "unknown"} (${aiStatus.audit?.model || "unknown"})`,
        aiStatus.lastFailover
          ? `Last failover: ${aiStatus.lastFailover.from} -> ${aiStatus.lastFailover.to} (${aiStatus.lastFailover.reason})`
          : "Last failover: none",
        aiStatus.fix ? `Fix: ${aiStatus.fix}` : ""
      ].filter(Boolean).join(" | ")
    : "AI status unavailable";

  useEffect(() => {
    let active = true;
    refreshSession().then((ok) => {
      if (!active) return;
      setAuthed(ok);
      setRole(getWorkspaceRole());
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!authed && location.pathname !== "/login") {
      navigate("/login", { replace: true });
    }
  }, [authed, location.pathname, navigate]);

  useEffect(() => {
    if (!notificationsEnabled || !workspaceId || !authed) return;
    const roleValue = String(role || "").toLowerCase();
    if (roleValue && roleValue === "viewer") return;
    const headers: Record<string, string> = {};
    const token = getAuthToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    fetch(`${getApiBase()}/workspaces/${workspaceId}/notifications/clio`, { credentials: "include", headers })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.suggestions) {
          setNotifications(data.suggestions);
        }
      })
      .catch(() => null);
  }, [workspaceId, authed, role, notificationsEnabled]);

  const launchStoryMode = () => {
    navigate("/");
    window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent("lexipro:story-mode"));
    }, 250);
  };

  return (
    <div className="min-h-screen w-full flex bg-[#0B0F14] text-[#CBD5E0] relative">
      {integrityAlert ? (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-red-950/80 backdrop-blur-sm">
          <div className="w-full max-w-2xl rounded-3xl border border-red-500/50 bg-red-950/90 p-8 text-red-100 shadow-2xl">
            <div className="text-[11px] uppercase tracking-[0.4em] text-red-300 mb-2">Breach Lockdown</div>
            <div className="text-lg font-semibold">CRITICAL ALERT: DETERMINISTIC_HEARTBEAT_DESYNC</div>
            <div className="mt-3 text-sm">{integrityAlert.message}</div>
            {integrityAlert.exhibitId ? (
              <div className="mt-2 font-mono text-[11px]">
                Exhibit: {integrityAlert.exhibitId} {integrityAlert.filename ? `(${integrityAlert.filename})` : ""}
              </div>
            ) : null}
            {integrityAlert.recordedHash && integrityAlert.actualHash ? (
              <div className="mt-3 font-mono text-[11px]">
                Expected: {integrityAlert.recordedHash.slice(0, 16)}... | Actual: {integrityAlert.actualHash.slice(0, 16)}...
              </div>
            ) : null}
            <div className="mt-3 text-[11px] text-red-200/80">
              Breach source: DEH-001 heartbeat verification (deterministic integrity gate).
            </div>
            <div className="mt-6 flex items-center gap-3">
              <button
                type="button"
                onClick={clearIntegrityAlert}
                className="rounded-full border border-red-400/60 bg-red-500/20 px-4 py-2 text-[11px] uppercase tracking-[0.3em] text-red-100"
              >
                Acknowledge Lockdown
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {null}
      <aside className="w-72 border-r border-white/10 bg-[#15171A]">
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
              {LOGO_URL ? (
                <img src={LOGO_URL} alt={`${APP_NAME} logo`} className="h-6 w-6 object-contain" />
              ) : (
                <svg viewBox="0 0 48 48" className="h-6 w-6" role="img" aria-label={`${APP_NAME} logo`} style={{ color: PRIMARY_COLOR }}>
                  <path
                    fill="currentColor"
                    d="M24 4c7.18 0 13 5.82 13 13s-5.82 13-13 13-13-5.82-13-13S16.82 4 24 4Zm0 4.5a8.5 8.5 0 1 0 0 17 8.5 8.5 0 0 0 0-17ZM9 38.5c0-4.14 6.27-7.5 14-7.5s14 3.36 14 7.5V42H9v-3.5Z"
                  />
                </svg>
              )}
            </div>
            <div className="lexipro-brand">
              <div className="text-sm font-semibold tracking-wide text-white">{APP_NAME}</div>
              <div className="text-[11px] text-slate-400 tracking-[0.24em] uppercase">Forensic OS</div>
            </div>
          </div>
          <div className="text-xs text-[#A0AEC0] mt-3">Deterministic, evidence-anchored AI</div>

          <div className="mt-4 flex flex-wrap gap-2">
            <Badge tone={authed ? "green" : "red"}>{authed ? "Authenticated" : "Signed out"}</Badge>
            {workspaceId ? (
              <div className="inline-flex items-center gap-2">
                <Badge tone="blue">Workspace: {workspaceName}</Badge>
                <span
                  className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-slate-300"
                  title={`Workspace ID: ${workspaceId}`}
                  aria-label="Workspace ID"
                >
                  <i className="fa-regular fa-copy" />
                </span>
              </div>
            ) : (
              <Badge tone="amber">No workspace</Badge>
            )}
          </div>

          <div className="mt-4 flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/login")}
              title="Open sign-in screen"
            >
              <i className="fa-solid fa-right-to-bracket" />
              Sign in
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                logout().finally(() => {
                  clearWorkspaceId();
                  setAuthed(false);
                  navigate("/login");
                });
              }}
              title="Clear session"
            >
              <i className="fa-solid fa-arrow-right-from-bracket" />
              Log out
            </Button>
          </div>
        </div>

        <nav className="p-3">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              id={item.id}
              className={({ isActive }) =>
                [
                  "flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition-colors border-l-2",
                  isActive
                    ? "bg-[#2563EB] text-white border-[#2563EB] shadow-[0_8px_20px_rgba(37,99,235,0.35)]"
                    : "text-[#94A3B8] hover:bg-white/5 hover:text-white border-transparent",
                ].join(" ")
              }
            >
              <i className={`${item.icon} w-5 text-center text-current text-[18px]`} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-3">
          <button
            className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-200 hover:bg-white/10"
            onClick={() => {
              setWorkspacePref(TOUR_PREF_KEY, "false", workspaceId).finally(() => {
                window.location.reload();
              });
            }}
            type="button"
          >
            Start Guided Tour
          </button>
          {isAdmin && import.meta.env.VITE_ENABLE_WALKTHROUGH === "1" ? (
            <button
              className="mt-3 w-full rounded-xl px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-white shadow-lg transition-transform hover:scale-[1.01]"
              style={{ background: "linear-gradient(120deg, #2563EB, #10B981)" }}
              onClick={launchStoryMode}
              type="button"
            >
              Launch Acquisition Walkthrough
            </button>
          ) : null}
        </div>
      </aside>

      <main className="flex-1 min-w-0 flex flex-col">
        <div className="h-14 border-b border-white/10 bg-[#1A1C1E] flex items-center justify-between px-6">
          <div className="text-sm text-[#CBD5E0]">
            {authed ? "Forensic session active" : "Sign in to enable uploads and AI"}
          </div>
          <div className="flex items-center gap-2">
            {!authed ? null : null}
            {isAdmin ? (
              <button
                type="button"
                onClick={() => setProofMode((prev) => !prev)}
                className={`rounded-full border px-3 py-1 text-[10px] uppercase tracking-[0.2em] ${
                  proofMode
                    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
                    : "border-white/10 bg-white/5 text-slate-300 hover:bg-white/10"
                }`}
                title="Toggle proof mode helper"
              >
                Proof Mode
              </button>
            ) : null}
            <div className="hidden md:flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-slate-300">
              <span>Workspace</span>
              <span className="text-white">{workspaceName}</span>
              <span className="mono text-slate-400">{workspaceId ? workspaceId.slice(0, 8) : "unassigned"}</span>
            </div>
            <IntegrityBadge />
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowNotifications((prev) => !prev)}
                className="relative h-9 w-9 rounded-full border border-white/10 bg-white/5 text-slate-200 hover:bg-white/10"
                title="Notifications"
              >
                <i className="fa-solid fa-bell" />
                {notifications.length ? (
                  <span className="absolute -top-1 -right-1 h-4 min-w-[16px] rounded-full bg-amber-400 text-[10px] font-bold text-black flex items-center justify-center px-1">
                    {notifications.length}
                  </span>
                ) : null}
              </button>
              {showNotifications ? (
                <div className="absolute right-0 mt-2 w-72 rounded-xl border border-white/10 bg-slate-950/95 p-3 text-xs text-slate-200 shadow-xl">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-2">Silent Partner</div>
                  {notifications.length ? (
                    notifications.map((item) => (
                      <div key={item.id} className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 mb-2">
                        {item.message}
                      </div>
                    ))
                  ) : (
                    <div className="text-slate-400">No new alerts.</div>
                  )}
                </div>
              ) : null}
            </div>
            <Badge tone={aiBadgeTone} title={aiTooltip}>
              {aiLabel} | Audit: ON
            </Badge>
            <Button
              variant="secondary"
              size="sm"
              className="border border-slate-700 bg-slate-800/80 text-[#E2E8F0] hover:bg-slate-700/80"
              onClick={() => navigate("/")}
            >
              <i className="fa-solid fa-upload" />
              Ingest
            </Button>
            <Button variant="primary" size="sm" onClick={() => navigate("/intelligence")}>
              <i className="fa-solid fa-bolt" />
              Analyze
            </Button>
          </div>
        </div>

        <div className="flex-1 min-h-0 p-6 overflow-auto">
          <TeleportProvider>
            <Outlet />
          </TeleportProvider>
        </div>
        {proofMode && isAdmin ? (
          <div className="fixed bottom-16 right-6 z-40 w-[280px] rounded-2xl border border-emerald-500/40 bg-black/80 p-4 text-xs text-emerald-100 shadow-xl">
            <div className="text-[10px] uppercase tracking-[0.35em] text-emerald-300">Proof Mode Checklist</div>
            <div className="mt-3 space-y-2">
              {proofSteps.map((step) => (
                <button
                  key={step.label}
                  type="button"
                  onClick={step.action}
                  className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-left text-[11px] text-emerald-100 hover:bg-white/10"
                >
                  {step.label}
                </button>
              ))}
            </div>
          </div>
        ) : null}
        <AigisFloatingBubble />
        <div className="fixed bottom-3 right-6 z-40 text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono">
          [ NODE: PONTIAC_L1 ] [ LATENCY: 42ms ] [ INTEGRITY: NOMINAL ]
        </div>
      </main>
    </div>
  );
}
